import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, UserCheck, Swords, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';

interface UpdateNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLightMode: boolean;
}

export const UpdateNoticeModal: React.FC<UpdateNoticeModalProps> = ({
  isOpen,
  onClose,
  isLightMode
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className={cn(
              "relative w-full max-w-md p-6 sm:p-8 rounded-3xl shadow-2xl overflow-hidden",
              isLightMode ? "bg-white text-[#1a1a1a]" : "bg-[#1a1a1a] text-white border border-white/10"
            )}
          >
            {/* Background decorative elements */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#ff0080]/10 rounded-full blur-3xl pointer-events-none" />
            
            <button
              onClick={onClose}
              className={cn(
                "absolute top-4 right-4 p-2 rounded-full transition-colors z-10",
                isLightMode ? "hover:bg-black/5" : "hover:bg-white/10"
              )}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative z-10">
              <div className="w-12 h-12 bg-[#ff0080] rounded-2xl flex items-center justify-center shadow-lg shadow-[#ff0080]/20 mb-6">
                <Sparkles className="w-6 h-6 text-white" />
              </div>

              <h2 className="text-2xl font-black tracking-tight mb-2">
                What's New in SongToSong
              </h2>
              <p className={cn("text-sm mb-8", isLightMode ? "text-gray-600" : "text-gray-400")}>
                We've rolled out some new features to improve the experience of the game. :)
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                    isLightMode ? "bg-black/5 text-[#ff0080]" : "bg-white/5 text-[#ff0080]"
                  )}>
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">User Accounts</h3>
                    <p className={cn("text-xs leading-relaxed", isLightMode ? "text-gray-600" : "text-gray-400")}>
                      Create an account to securely save your stats and sync your daily streak across all your devices!
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                    isLightMode ? "bg-black/5 text-[#ff0080]" : "bg-white/5 text-[#ff0080]"
                  )}>
                    <Swords className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Custom Challenges</h3>
                    <p className={cn("text-xs leading-relaxed", isLightMode ? "text-gray-600" : "text-gray-400")}>
                      Challenge your friends! Create custom puzzles with any start and end song and share the link.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full py-4 rounded-full font-bold text-base flex items-center justify-center gap-2 bg-[#ff0080] text-white shadow-lg shadow-[#ff0080]/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                <span>Awesome, let's play</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
